"use client";

import { format, subMonths } from "date-fns";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ReactNode, createContext, useContext } from "react";
import { Range as RangeDate } from "react-date-range";

type ProviderProps = {
  children: ReactNode;
};

type FormattedDates = {
  startDate: string;
  endDate: string;
};

type Dates = {
  startDate: Date;
  endDate: Date;
};

type SelectedDatesContextType = {
  selectedDates: Dates;
  formattedDates: FormattedDates;
  handleDatesUpdate: (dates: RangeDate) => void;
};

const initDates = {
  startDate: subMonths(new Date(), 1),
  endDate: new Date(),
};

const getFormattedDates = (dates: Dates) => {
  const startDate = dates.startDate
    ? format(dates.startDate, "yyyy-MM-dd")
    : "";
  const endDate = dates.endDate ? format(dates.endDate, "yyyy-MM-dd") : "";
  return { startDate, endDate };
};

const initFormattedDates = getFormattedDates(initDates);

const SelectedDatesContext = createContext<SelectedDatesContextType>({
  selectedDates: initDates,
  handleDatesUpdate: () => {},
  formattedDates: initFormattedDates,
});

const START_DATE_KEY = "start-date";
const END_DATE_KEY = "end-date";

export const SelectedDatesProvider = ({ children }: ProviderProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const startDate =
    searchParams?.get(START_DATE_KEY) || initFormattedDates.startDate;
  const endDate = searchParams?.get(END_DATE_KEY) || initFormattedDates.endDate;

  const formattedDates: FormattedDates = {
    startDate,
    endDate,
  };

  const selectedDates: Dates = {
    startDate: new Date(startDate),
    endDate: new Date(endDate),
  };

  const handleDatesUpdate = (dates: RangeDate) => {
    const { startDate: newStart, endDate: newEnd } = dates;
    if (newStart && newEnd) {
      const newFormattedDates = getFormattedDates({
        startDate: newStart,
        endDate: newEnd,
      });

      const params = new URLSearchParams(searchParams?.toString() || "");
      params.set(START_DATE_KEY, newFormattedDates.startDate);
      params.set(END_DATE_KEY, newFormattedDates.endDate);

      router.push(`${pathname}?${params.toString()}`);
    }
  };

  return (
    <SelectedDatesContext.Provider
      value={{
        handleDatesUpdate,
        selectedDates,
        formattedDates,
      }}
    >
      {children}
    </SelectedDatesContext.Provider>
  );
};

export const useSelectedDatesContext = () => useContext(SelectedDatesContext);
